/**
 * emailHeaderFootersAdmin.service.ts — Admin > Forms/Templates > Header &
 * Footer (legacy E-mail Header & Footer): the HTML wrapped above and below an
 * email's body, in the account's order, through /email-header-footers. Tasks,
 * auto responders and the compose window pick one of the live ones. Writes need
 * Admin Access.
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface AdminHeaderFooter {
  id: number;
  name: string;
  description: string;
  header: string;
  footer: string;
  active: boolean;
  order: number;
}

export type HeaderFooterPayload = Pick<AdminHeaderFooter, 'name' | 'description' | 'header' | 'footer'>;

/** The API's limits (legacy set none). */
export const HEADER_FOOTER_NAME_MAX = 255;
export const HEADER_FOOTER_DESCRIPTION_MAX = 255;

function toHeaderFooter(raw: Record<string, unknown>): AdminHeaderFooter {
  return {
    id: Number(raw.emailHeaderFooterID) || 0,
    name: String(raw.name ?? ''),
    description: String(raw.description ?? ''),
    header: String(raw.header ?? ''),
    footer: String(raw.footer ?? ''),
    active: raw.active === true || raw.active === 1 || raw.active === '1',
    order: Number(raw.ehOrder) || 0,
  };
}

/** Every template, deleted ones too, in the account's order. */
export function listHeaderFooters(): Promise<AdminHeaderFooter[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/email-header-footers')
    .then((r) => (r.data.data ?? []).map(toHeaderFooter));
}

function body(data: HeaderFooterPayload): HeaderFooterPayload {
  return { name: data.name.trim(), description: data.description.trim(), header: data.header, footer: data.footer };
}

export function createHeaderFooter(data: HeaderFooterPayload): Promise<void> {
  return apiClient.post('/email-header-footers', body(data)).then(() => undefined);
}

export function updateHeaderFooter(id: number, data: HeaderFooterPayload): Promise<void> {
  return apiClient.put(`/email-header-footers/${id}`, body(data)).then(() => undefined);
}

export function deleteHeaderFooter(id: number): Promise<void> {
  return apiClient.delete(`/email-header-footers/${id}`).then(() => undefined);
}

export function restoreHeaderFooter(id: number): Promise<void> {
  return apiClient.post(`/email-header-footers/${id}/restore`).then(() => undefined);
}

/** The live templates, in their new order. */
export function reorderHeaderFooters(ids: number[]): Promise<void> {
  return apiClient.post('/email-header-footers/reorder', { ids }).then(() => undefined);
}

/** Legacy's preview: the header, a sample body, then the footer. */
export function headerFooterPreviewHtml(header: string, footer: string): string {
  return `${header}<div style="padding:24px 0;color:#64748b;font-family:sans-serif;font-size:14px;"><strong>Body:</strong> the email's own content goes here.</div>${footer}`;
}
